import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    Table,
    Tag,
    Input,
    Select,
    Button,
    Space,
    message,
    Alert,
    Tooltip,
    Statistic,
    Row,
    Col,
} from "antd";
import {
    ReloadOutlined,
    SearchOutlined,
    MailOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import CopyText from "../components/CopyText";

// Why a batch sent nothing — shown in plain English instead of a silent no-op.
const REASONS = {
    mail_not_configured:
        "EMAIL_USER / EMAIL_PASS are not set on the server, so no mail can be sent.",
    no_present_participants:
        "Nobody was marked present for that event (QR scan or manual), and certificates only go to attendees.",
    already_sent: "Certificates were already sent for that event.",
    not_completed: "The event is not completed yet.",
    all_failed: "Every send attempt failed — check the errors in the table.",
    ok: "Certificates sent.",
};

const CertificatesPage = () => {
    const [data, setData] = useState({ items: [], sent: 0, failed: 0 });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listCertificates({ status, q });
            setData(res);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [status, q]);

    useEffect(() => {
        load();
    }, [load]);

    const resend = async (eventId, eventTitle) => {
        try {
            const r = await api.sendCertificates(eventId);
            if (r.sent > 0) {
                message.success(`Sent ${r.sent} certificate(s) for “${eventTitle}”`);
            } else if (r.failed > 0) {
                // Surface the failure loudly; the row's Error column has the detail.
                message.error({
                    content: `All ${r.failed} send(s) failed for “${eventTitle}”. See the Error column below.`,
                    duration: 6,
                });
            } else {
                message.warning(REASONS[r.reason] || "Nothing was sent");
            }
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    // Diagnose delivery directly: verifies the SMTP connection then sends a
    // plain test email, reporting the raw error if either step fails.
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const runTest = async () => {
        if (!testTo.trim()) return message.warning("Enter an email address");
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.sendTestEmail(testTo.trim());
            setTestResult(r);
            if (r.ok) message.success("Test email sent");
        } catch (e) {
            setTestResult({ ok: false, error: e.message });
        } finally {
            setTesting(false);
        }
    };

    const columns = [
        {
            title: "Sent at",
            dataIndex: "sentAt",
            key: "sentAt",
            width: 170,
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Volunteer",
            key: "who",
            render: (_, r) => (
                <Space>
                    <span>{r.userName || "—"}</span>
                    {r.isLead && <Tag color="gold">★ lead</Tag>}
                </Space>
            ),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (v) =>
                v ? (
                    <CopyText value={v}>
                        <span>
                            <MailOutlined /> {v}
                        </span>
                    </CopyText>
                ) : (
                    "—"
                ),
        },
        { title: "Event", dataIndex: "eventTitle", key: "eventTitle", render: (v) => v || "—" },
        {
            title: "Certificate ID",
            dataIndex: "certificateId",
            key: "certificateId",
            render: (v) => (v ? <CopyText value={v}><code>{v}</code></CopyText> : "—"),
        },
        {
            title: "Status",
            key: "status",
            width: 100,
            render: (_, r) =>
                r.status === "sent" ? (
                    <Tag color="green">Sent</Tag>
                ) : (
                    <Tag color="red">Failed</Tag>
                ),
        },
        {
            title: "Error",
            dataIndex: "error",
            key: "error",
            width: 320,
            render: (v) =>
                v ? (
                    <Tooltip title={v}>
                        <span style={{ color: "#dc2626", fontSize: 12 }}>{v}</span>
                    </Tooltip>
                ) : (
                    ""
                ),
        },
        {
            title: "",
            key: "actions",
            width: 130,
            align: "right",
            render: (_, r) =>
                r.event ? (
                    <Button
                        size="small"
                        icon={<SendOutlined />}
                        onClick={() => resend(r.event, r.eventTitle)}
                    >
                        Re-send
                    </Button>
                ) : null,
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Certificates</h1>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                    Refresh
                </Button>
            </div>

            {data.mailConfigured === false && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Email is not configured — no certificates can be sent"
                    description="Set MAIL_RELAY_URL and MAIL_RELAY_KEY (recommended — this host cannot reach Gmail directly), or EMAIL_USER and EMAIL_PASS, in this service's environment variables on Render, then redeploy."
                />
            )}
            {data.mailConfigured && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`Certificates are sent from ${data.mailFrom}${data.mailMode === "relay" ? " (via the app backend relay)" : ""}`}
                    description="Certificates go only to volunteers marked present (QR scan or manual). If an event sent nothing, check its attendance first, then use Re-send."
                />
            )}

            <Card
                variant="borderless"
                title="Diagnose delivery"
                style={{ marginBottom: 16 }}
            >
                <Space wrap>
                    <Input
                        style={{ width: 300 }}
                        placeholder="your.address@example.com"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        onPressEnter={runTest}
                    />
                    <Button type="primary" loading={testing} onClick={runTest}>
                        Send test email
                    </Button>
                </Space>
                {testResult && (
                    <Alert
                        style={{ marginTop: 12 }}
                        type={testResult.ok ? "success" : "error"}
                        showIcon
                        message={
                            testResult.ok
                                ? "Test email sent successfully — delivery works."
                                : testResult.stage === "connection"
                                ? "Could not connect / authenticate to the mail server"
                                : "Connected, but sending failed"
                        }
                        description={
                            testResult.ok ? null : (
                                <>
                                    <div style={{ fontFamily: "monospace", marginBottom: 8 }}>
                                        {testResult.error}
                                    </div>
                                    <div style={{ fontSize: 12 }}>
                                        “Username and Password not accepted” means EMAIL_PASS is
                                        not a valid Gmail <b>App Password</b> (a normal account
                                        password will not work, and 2-Step Verification must be
                                        on). A connection timeout usually means this service is
                                        running a Node version whose IPv6 route to Gmail is
                                        unreachable — set NODE_VERSION=20 and redeploy.
                                    </div>
                                </>
                            )
                        }
                    />
                )}
            </Card>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col>
                    <Card variant="borderless">
                        <Statistic title="Sent" value={data.sent || 0} valueStyle={{ color: "#16a34a" }} />
                    </Card>
                </Col>
                <Col>
                    <Card variant="borderless">
                        <Statistic title="Failed" value={data.failed || 0} valueStyle={{ color: "#dc2626" }} />
                    </Card>
                </Col>
            </Row>

            <Card variant="borderless">
                <Space style={{ marginBottom: 16 }} wrap>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Search name, email or event"
                        style={{ width: 280 }}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <Select
                        allowClear
                        placeholder="Status"
                        style={{ width: 140 }}
                        value={status || undefined}
                        onChange={(v) => setStatus(v || "")}
                        options={[
                            { value: "sent", label: "Sent" },
                            { value: "failed", label: "Failed" },
                        ]}
                    />
                </Space>

                <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    columns={columns}
                    dataSource={data.items || []}
                    scroll={{ x: "max-content" }}
                    locale={{
                        emptyText:
                            "No certificate emails yet. Certificates are sent when an event is completed AND attendees are marked present. To send them now: Events → Attendees → Send certificates.",
                    }}
                    pagination={{ pageSize: 20, showTotal: (t) => `${t} emails` }}
                />
            </Card>
        </div>
    );
};

export default CertificatesPage;
